import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import styled from '@emotion/styled';
import { AdminPageContainer, StaticCard } from '../../../components/admin/sharedStyles';
import PageHeader from '../../../components/PageHeader';
import ConfigSubMenu from '../../../components/admin/ConfigSubMenu';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { getConfigValue, saveConfig } from '../../../redux/slices/config';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';

const LoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const SectionCard = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const SaveActions = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
});

const StorageConfig = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [formData, setFormData] = useState({
        provider: 's3',
        bucket: '',
        projectId: '',
        region: '',
        accessKeyId: '',
        secretAccessKey: '',
        tempFileTtlHours: 1,
        readNotificationPurgeDays: 30,
    });

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = () => {
        setLoading(true);
        dispatch(getConfigValue({
            key: 'storageConfig',
            value: null,
            callback: (data) => {
                if (data?.value) {
                    const val = data.value;
                    setFormData({
                        provider: val.provider || 's3',
                        bucket: val.bucket || '',
                        projectId: val.projectId || '',
                        region: val.region || '',
                        accessKeyId: val.accessKeyId || '',
                        secretAccessKey: val.secretAccessKey || '',
                        tempFileTtlHours: val.tempFileTtlHours ?? 1,
                        readNotificationPurgeDays: val.readNotificationPurgeDays ?? 30,
                    });
                }
                setLoading(false);
            }
        }));
    };

    const { showPrompt, confirmLeave, cancelLeave, message: unsavedMessage } = useUnsavedChanges(isDirty);

    const handleChange = (e) => {
        setIsDirty(true);
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = () => {
        const saveData = {
            provider: formData.provider,
            bucket: formData.bucket,
            projectId: formData.projectId,
            region: formData.region,
            accessKeyId: formData.accessKeyId || null,
            secretAccessKey: formData.secretAccessKey || null,
            tempFileTtlHours: Number(formData.tempFileTtlHours) || 1,
            readNotificationPurgeDays: Number(formData.readNotificationPurgeDays) || 30,
        };

        setSaving(true);
        dispatch(saveConfig({
            key: 'storageConfig',
            value: saveData,
            callback: (data) => {
                setSaving(false);
                if (data?.status === 0) return;
                setIsDirty(false);
                dispatch(showAlert({ severity: 'success', message: 'Storage configuration saved successfully' }));
            }
        }));
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Configuration"
                subtitle="Manage system configuration settings"
            />

            <ConfigSubMenu />

            {loading ? (
                <LoadingBox>
                    <CircularProgress />
                </LoadingBox>
            ) : (
                <>
                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Storage Provider
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Provider</InputLabel>
                                    <Select
                                        name="provider"
                                        value={formData.provider}
                                        onChange={handleChange}
                                        label="Provider"
                                    >
                                        <MenuItem value="s3">Amazon S3</MenuItem>
                                        <MenuItem value="gc">Google Cloud Storage</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Bucket Name"
                                    name="bucket"
                                    value={formData.bucket}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Region"
                                    name="region"
                                    value={formData.region}
                                    onChange={handleChange}
                                    placeholder="us-east-1"
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Retention Policy
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Temp file TTL (hours)"
                                    name="tempFileTtlHours"
                                    type="number"
                                    value={formData.tempFileTtlHours}
                                    onChange={handleChange}
                                    inputProps={{ min: 1, step: 1 }}
                                    helperText="How long to keep files in temp/ before the daily maintenance job purges them"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Read notification retention (days)"
                                    name="readNotificationPurgeDays"
                                    type="number"
                                    value={formData.readNotificationPurgeDays}
                                    onChange={handleChange}
                                    inputProps={{ min: 1, step: 1 }}
                                    helperText="Delete read notifications older than this"
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    {formData.provider === 's3' && (
                        <SectionCard>
                            <Typography variant="h6" gutterBottom>
                                S3 Credentials
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Access Key ID"
                                        name="accessKeyId"
                                        value={formData.accessKeyId}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Secret Access Key"
                                        name="secretAccessKey"
                                        type="password"
                                        value={formData.secretAccessKey}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            </Grid>
                        </SectionCard>
                    )}

                    {formData.provider === 'gc' && (
                        <SectionCard>
                            <Typography variant="h6" gutterBottom>
                                Google Cloud Credentials
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Project ID"
                                        name="projectId"
                                        value={formData.projectId}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Alert severity="info">
                                        The service account key is read from a file mounted into the container at <code>GOOGLE_SERVICE_STORAGE_KEY_FILE</code>. Update the mount in <code>docker-compose.yml</code> to rotate the key.
                                    </Alert>
                                </Grid>
                            </Grid>
                        </SectionCard>
                    )}

                    <SaveActions>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </SaveActions>
                </>
            )}
            <ConfirmDialog
                open={showPrompt}
                onClose={cancelLeave}
                onConfirm={confirmLeave}
                title="Unsaved Changes"
                message={unsavedMessage}
                confirmLabel="Leave"
                confirmColor="warning"
            />
        </AdminPageContainer>
    );
};

export default StorageConfig;
